/**
 * Keyboard and gamepad (docs/spec/07 §3). Game input goes through the `DirectionLatch`; menus get
 * `MenuAction`s with key repeat; raw keys go to the scene first so text entry and rebinding work.
 */

import type { Action, OptionsStore } from '../platform/options';
import { ACTIONS, FIXED_FIRE } from '../platform/options';
import type { Dir } from '../sim/types';
import { DIR_DOWN, DIR_LEFT, DIR_RIGHT, DIR_UP } from '../sim/types';
import type { KeyInput, MenuAction } from '../ui/scene';
import { DirectionLatch } from './latch';

export interface ControlsHandlers {
  /** Raw key first; true = consumed, no action follows. */
  key(k: KeyInput): boolean;
  action(a: MenuAction): void;
  /** Any key, button or touch at all (audio unlock, idle timers). */
  any(): void;
}

const DIRS: Partial<Record<Action, Dir>> = { up: DIR_UP, down: DIR_DOWN, left: DIR_LEFT, right: DIR_RIGHT };
const REPEAT_DELAY = 360;
const REPEAT_RATE = 95;
const PAD_DEAD = 0.5;

export class Controls {
  readonly latch = new DirectionLatch();
  private handlers: ControlsHandlers | null = null;
  private repeat: { source: string; action: MenuAction; wait: number } | null = null;
  private padHeld = new Set<string>();
  private padSeen = false;

  constructor(private readonly options: OptionsStore) {}

  attach(handlers: ControlsHandlers): void {
    this.handlers = handlers;
    window.addEventListener('keydown', (e) => this.down(e));
    window.addEventListener('keyup', (e) => this.up(e.code));
    window.addEventListener('blur', () => {
      this.latch.reset();
      this.repeat = null;
    });
    window.addEventListener('gamepadconnected', () => {
      this.padSeen = true;
    });
  }

  get gamepad(): boolean {
    return this.padSeen;
  }

  /** The game action a key is bound to, if any. */
  actionOf(code: string): Action | null {
    const keys = this.options.get().keys;
    for (const a of ACTIONS) if (keys[a].includes(code)) return a;
    return FIXED_FIRE.includes(code) ? 'fire' : null;
  }

  private down(e: KeyboardEvent): void {
    const h = this.handlers;
    if (!h) return;
    h.any();
    const action = this.actionOf(e.code);
    const menuKey = action !== null || ['Enter', 'NumpadEnter', 'Escape', 'Backspace', 'Tab'].includes(e.code);
    if (e.repeat) {
      if (menuKey) e.preventDefault();
      return;
    }
    if (h.key({ code: e.code, key: e.key, shift: e.shiftKey, ctrl: e.ctrlKey || e.metaKey })) {
      e.preventDefault();
      return;
    }
    if (!menuKey) return;
    e.preventDefault();
    const source = `k:${e.code}`;
    const dir = action ? DIRS[action] : undefined;
    if (dir !== undefined && action) {
      this.latch.press(source, dir);
      this.fire(action as MenuAction, source);
    } else if (action === 'fire') {
      this.latch.fireDown(source);
      h.action('confirm');
    } else if (e.code === 'Escape' || e.code === 'Backspace') h.action('back');
    else if (e.code === 'Enter' || e.code === 'NumpadEnter') h.action('confirm');
    else if (action === 'pause') h.action('pause');
  }

  private up(code: string): void {
    const source = `k:${code}`;
    this.latch.release(source);
    this.latch.fireUp(source);
    if (this.repeat?.source === source) this.repeat = null;
  }

  /** Emit a direction action now and keep repeating it while `source` is held. */
  private fire(action: MenuAction, source: string): void {
    this.handlers?.action(action);
    this.repeat = { source, action, wait: REPEAT_DELAY };
  }

  /** Call every frame: key repeat and the gamepad. */
  update(dtMs: number): void {
    this.pollPad();
    const r = this.repeat;
    if (!r) return;
    r.wait -= dtMs;
    while (r.wait <= 0 && this.repeat === r) {
      r.wait += REPEAT_RATE;
      this.handlers?.action(r.action);
    }
  }

  private pollPad(): void {
    const pads = typeof navigator.getGamepads === 'function' ? navigator.getGamepads() : [];
    const pad = [...pads].find((p) => p !== null && p.connected);
    const now = new Set<string>();
    if (pad) {
      this.padSeen = true;
      const b = (i: number): boolean => pad.buttons[i]?.pressed === true;
      const ax = pad.axes[0] ?? 0;
      const ay = pad.axes[1] ?? 0;
      if (b(12) || ay < -PAD_DEAD) now.add('up');
      if (b(13) || ay > PAD_DEAD) now.add('down');
      if (b(14) || ax < -PAD_DEAD) now.add('left');
      if (b(15) || ax > PAD_DEAD) now.add('right');
      if (b(0) || b(2)) now.add('fire');
      if (b(1) || b(3)) now.add('back');
      if (b(9)) now.add('pause');
    }
    for (const name of now) {
      if (this.padHeld.has(name)) continue;
      this.handlers?.any();
      const source = `p:${name}`;
      const dir = DIRS[name as Action];
      if (dir !== undefined) {
        this.latch.press(source, dir);
        this.fire(name as MenuAction, source);
      } else if (name === 'fire') {
        this.latch.fireDown(source);
        this.handlers?.action('confirm');
      } else this.handlers?.action(name as MenuAction);
    }
    for (const name of this.padHeld) {
      if (now.has(name)) continue;
      const source = `p:${name}`;
      this.latch.release(source);
      this.latch.fireUp(source);
      if (this.repeat?.source === source) this.repeat = null;
    }
    this.padHeld = now;
  }
}
